/**
 * Client-side image optimisation — Architecture §11.1.
 *
 * Resizing in the browser is what keeps the product off a paid image service
 * and keeps R2 storage small: a modern phone photo is 4-8 MB, and the report
 * needs about 300 KB of it.
 *
 * This is a convenience, not a security boundary. The Worker validates type
 * and size again on arrival (Architecture §11.2).
 */

/** Doc 6 / Constitution §9: 1600-2000px on the long edge is enough for print. */
const MAX_DIMENSION = 1800;

/** What a successfully-compressed photo should land near. */
const TARGET_BYTES = 450 * 1024;

/**
 * The ceiling after compression, matching the Worker's own backstop
 * (`MAX_UPLOAD_BYTES` in apps/api/src/storage/media.ts — keep both in sync).
 * Generous on purpose: a studio reported a real, ordinary phone photo
 * (well under 3 MB to begin with) still getting rejected at the old 2 MB
 * ceiling, most likely a dim/high-detail shot the quality ladder above
 * couldn't compress enough in four steps. Rejecting a real photo the
 * instructor is standing there trying to upload is worse than storing one
 * a few MB larger than the 450 KB target.
 */
const HARD_LIMIT_BYTES = 20 * 1024 * 1024;

/**
 * The raw file a phone hands us, before any resizing — not what ends up
 * stored. A newer iPhone's ProRAW or high-resolution modes can produce a
 * 50-100 MB original; rejecting those here would block a real, legitimate
 * photo for no benefit, since the compressed result a few lines down is what
 * actually gets uploaded and stored, and that stays small regardless of how
 * large the original was. This only guards against a genuinely pathological
 * selection (the wrong file entirely).
 */
const MAX_INPUT_BYTES = 100 * 1024 * 1024;

const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52] as const;

export interface OptimizedImage {
  file: File;
  width: number;
  height: number;
}

export class ImageError extends Error {
  readonly code: 'INVALID_IMAGE_TYPE' | 'IMAGE_TOO_LARGE' | 'UPLOAD_FAILED';

  constructor(code: ImageError['code'], message: string) {
    super(message);
    this.name = 'ImageError';
    this.code = code;
  }
}

const ACCEPTED_INPUT = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Loads, resizes and re-encodes an image.
 *
 * Aspect ratio is preserved exactly — a distorted progress photo is a
 * misleading one (Constitution §7.2, Doc 6 §15.3). Only the long edge is
 * capped; the short edge follows.
 */
export async function optimizeImage(input: File): Promise<OptimizedImage> {
  if (input.type && !ACCEPTED_INPUT.includes(input.type)) {
    throw new ImageError('INVALID_IMAGE_TYPE', 'Unsupported image type.');
  }

  // Guard before decoding: a huge file would otherwise be decoded into memory
  // just to be rejected.
  if (input.size > MAX_INPUT_BYTES) {
    throw new ImageError('IMAGE_TOO_LARGE', 'Image is too large.');
  }

  let bitmap: ImageBitmap;
  try {
    // imageOrientation honours the EXIF rotation a phone camera writes, so a
    // portrait photo is not stored on its side.
    bitmap = await createImageBitmap(input, { imageOrientation: 'from-image' });
  } catch {
    throw new ImageError('INVALID_IMAGE_TYPE', 'Image could not be read.');
  }

  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new ImageError('UPLOAD_FAILED', 'Canvas is unavailable.');

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // Re-encoding through a canvas also drops EXIF, and with it the GPS
  // coordinates a phone may have written into a photograph of a client
  // (Constitution §10: collect only what the product needs).
  let blob: Blob | null = null;
  for (const quality of QUALITY_STEPS) {
    blob = await canvasToBlob(canvas, 'image/webp', quality);
    if (blob && blob.size <= TARGET_BYTES) break;
  }

  if (!blob) {
    // Very old browsers may not encode WebP from a canvas.
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
  }
  if (!blob) throw new ImageError('UPLOAD_FAILED', 'Image could not be encoded.');

  if (blob.size > HARD_LIMIT_BYTES) {
    throw new ImageError('IMAGE_TOO_LARGE', 'Image is too large after compression.');
  }

  const extension = blob.type === 'image/webp' ? 'webp' : 'jpg';
  const file = new File([blob], `photo.${extension}`, { type: blob.type });

  return { file, width, height };
}
